import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import { formValueSelector, type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { titleCase } from "title-case";
import { match } from "ts-pattern";
import { Callout, CalloutIntent, DialogComponent } from "widgets";

import { getDisabledPrjCapabilityTooltipMessage, prjPrivateSavingAllowed } from "@/app-utils/capability.ts";
import { simpleFieldValidation } from "@/app-utils/form.ts";
import { getSavedItemTypeLabel } from "@/app-utils/string.ts";
import {
  type InputModifySavedItemInput,
  type Project,
  type SavedItem,
  type SavedItemsListing,
  type User,
} from "@/graphql.ts";
import type { ProjectBasisFragment } from "@/modules/projects/graphql/projects-queries.gql.js";
import type { SavedItemFragment } from "@/modules/saved-items/graphql/saved-items.gql.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { updateSavedItem } from "@/redux/modules/saved-group.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { CAPABILITY } from "@/settings/settings.ts";
import { ProjectTagInputReduxFormField } from "@/ui-modules/core/redux-form/ProjectTagInputField.tsx";

import CheckboxField, { CheckboxReduxFormField } from "../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";

interface IEditSavedItemDialogOwnProps {
  savedItem: SavedItemFragment;
  itemList: SavedItemsListing; // to know the context, e.g. for the type label because displayName is not fitting here
  project?: ProjectBasisFragment | null;

  onApply: (editedItem: SavedItemFragment) => void;
  onClose: () => void;
}

interface IEditSavedItemDialogAppStateProps {
  currentUser: User; // only the current user is able to change the privacy flag
  initialValues: Partial<FormData>;
  currentIsPrivate: boolean;
  project: Project;
}

interface IEditSavedItemDialogDispatchProps {
  updateSavedItem: (data: InputModifySavedItemInput) => Promise<SavedItem>;
}

export interface IEditSavedItemDialogProps
  extends IEditSavedItemDialogAppStateProps,
    IEditSavedItemDialogDispatchProps,
    IEditSavedItemDialogOwnProps,
    InjectedFormProps<FormData, any> {}

function mapStateToProps(state: IAppState, ownProps: IEditSavedItemDialogProps): IEditSavedItemDialogAppStateProps {
  return {
    currentUser: state.auth.session,
    currentIsPrivate: formValueSelector(EditSavedItemDialogImpl.FORM_NAME)(
      state,
      EditSavedItemDialogImpl.FIELD_ISPRIVATE,
    ),
    initialValues: {
      [EditSavedItemDialogImpl.FIELD_NAME]: ownProps.savedItem.name,
      [EditSavedItemDialogImpl.FIELD_DESCRIPTION]: ownProps.savedItem.description,
      [EditSavedItemDialogImpl.FIELD_TAGS]: ownProps.savedItem.itemTags.map((tag) => tag.id),
      [EditSavedItemDialogImpl.FIELD_ISPRIVATE]: ownProps.savedItem.project.isPrivateWorkspace
        ? false
        : !ownProps.savedItem.shared,
    },
    project:
      ownProps.project ?? state.projectList.projects.find((p: Project) => p.id === ownProps.savedItem.project.id),
  };
}

function mapDispatchToProps(dispatch: ReduxDispatch): IEditSavedItemDialogDispatchProps {
  return {
    updateSavedItem: (data: InputModifySavedItemInput): Promise<SavedItem> => dispatch(updateSavedItem(data)),
  };
}

class EditSavedItemDialogImpl extends Component<IEditSavedItemDialogProps> {
  public static FORM_NAME = "editSavedItem";

  public static FIELD_NAME = "name";
  public static FIELD_ISPRIVATE = "isPrivate";
  public static FIELD_DESCRIPTION = "description";
  public static FIELD_TAGS = "tags";

  constructor(props: IEditSavedItemDialogProps) {
    super(props);
  }

  // map  this.props.entity.type -> map of field name to max length of fields
  getLengthMap() {
    return match(this.props.savedItem)
      .with({ __typename: "View" }, (_item) => {
        return DB_FIELD_MAX_LENGTH.VIEW;
      })
      .with({ __typename: "RegionFlag" }, (_item) => {
        return DB_FIELD_MAX_LENGTH.FLAG;
      })
      .otherwise((_savedItem) => {
        return DB_FIELD_MAX_LENGTH.GROUP;
      });
  }

  handleSubmitData = (values: FormData): Promise<any> => {
    const updatedEntity: InputModifySavedItemInput = {
      id: this.props.savedItem.id,
      type: this.props.savedItem.type,
      name: values[EditSavedItemDialogImpl.FIELD_NAME],
      description: values[EditSavedItemDialogImpl.FIELD_DESCRIPTION],
      itemTagIds: values[EditSavedItemDialogImpl.FIELD_TAGS],
      shared: !this.props.savedItem.project.isPrivateWorkspace
        ? !values[EditSavedItemDialogImpl.FIELD_ISPRIVATE]
        : this.props.savedItem.shared,
    };

    return this.props.updateSavedItem(updatedEntity).then(
      (fulfilled) => {
        this.props.onApply(fulfilled);
        this.props.onClose();
        this.props.reset();
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error,
      // with a general error that will be available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  renderPrivacyCheckbox() {
    /*
     The "Private (Hide from other project members)" option can be disabled by the project admin or creator
     if it is currently private. However, a shared item cannot be switched to public. If the item is
     currently shared with project members, the option will be disabled and a tooltip will indicate
     "Items that have already been shared with project members cannot be made public. You may delete
     this item if you no longer with to share it with the project."

     Levels of privacy:
     A)    entity in your Private Workspace  -> visible to you, shared is ignored, there are no project members (besides you)
     B1)   entity in a private project, entity is not shared  -> visible to you
     B2)   entity in a private project, entity is shared  -> visible to you!
     C1)   entity in a project, entity is shared  -> visible to project members
     C2)   entity in a project, entity is not shared  -> visible to you
     D1)   entity in a public project, entity is shared -> visible to every one
     D2)   entity in a public project, entity is not shared' -> visible to you

     ||  Project                          ||  prj.isPublic  || members ||   e.shared || can change? ||  display   ||
     ||                                   ||                ||         ||            ||             || checkbox?  ||
     ||-----------------------------------------------------------------------------------------------------------||
     || Private Workspace                 ||     false      || you     ||     false  ||  no         ||  on+disabled  ||
     ||                                   ||                ||         ||     true   ||  no         ||  ON+disabled  ||
     ||                                   ||                ||         ||            ||             ||            ||
     || Private Project (just you)        ||     false      || you     ||     false  ||  yes?       ||  enabled   ||
     ||                                   ||                ||         ||     true   ||  no         ||  disabled  ||
     ||                                   ||                ||         ||            ||             ||            ||
     || Project with N members            ||     false      || you + N ||     false  ||  yes        ||  enabled   ||
     ||                                   ||                ||         ||     true   ||  no         ||  disabled  ||
     ||                                   ||                ||         ||            ||             ||            ||
     || Project for all users (Public)    ||     true       || all     ||     false  ||  yes        ||  enabled   ||
     ||                                   ||                ||         ||     true   ||  no         ||  disabled  ||

     Disabled checkboxes have an explaining tooltip:
     * Private Workspace -> 'never shared'
     * Private Project (just you as a member) -> ? publishing is pointless, but possible for consistency. (no tooltip)
     * Is already shared (and not in private workspace) -> 'cannot hide shared items'
     */

    const savedItem: SavedItemFragment = this.props.savedItem;
    const privateSavingAllowed: boolean = prjPrivateSavingAllowed(this.props.project);

    const isOwner: boolean = this.props.currentUser.username === savedItem.owner;
    const privateSavingActive: boolean =
      isOwner && // only able to change the "privacy" property  if user is the owner
      !savedItem.project.isPrivateWorkspace && // 'publish' is meaningless in a Private Workspace
      !savedItem.shared && // You can 'publish' but not 'hide'
      (privateSavingAllowed || this.props.currentIsPrivate); // if project capability allows it or (if not) the saved item is private (allowing users to make it public)

    const privateFieldLabel = `Private (Hidden from other ${getProjectLabel(false, false)} members)`;

    let toolTipTextForIsPrivate: string = privateFieldLabel;
    if (savedItem.shared) {
      toolTipTextForIsPrivate =
        `Items that have already been shared with ${getProjectLabel(false, false)} members cannot be made private. ` +
        `You may delete this item if you no longer want to share it with the ${getProjectLabel(false, false)}.`;
    } else if (savedItem.project.isPrivateWorkspace) {
      toolTipTextForIsPrivate = "Items in the Private Workspace are never shared with other users.";
    } else if (!isOwner) {
      toolTipTextForIsPrivate = "Only the owner of the item can publish it";
    } else if (!privateSavingAllowed && !this.props.currentIsPrivate) {
      // show this tooltip only if the checkbox is switched off due to missing "private saving" capability (see above)
      toolTipTextForIsPrivate = getDisabledPrjCapabilityTooltipMessage(CAPABILITY.PRIVATE_SAVING);
    }

    return (
      <CheckboxReduxFormField
        component={CheckboxField}
        name={EditSavedItemDialogImpl.FIELD_ISPRIVATE}
        label={privateFieldLabel}
        disabled={!privateSavingActive}
        tooltipText={toolTipTextForIsPrivate}
        style={{ marginTop: 0 }}
      />
    );
  }

  render(): ReactElement {
    const { savedItem } = this.props;

    if (!savedItem?.id) {
      return null;
    }

    const titleMessage: string = "Edit " + titleCase(getSavedItemTypeLabel(this.props.itemList));

    const lengthMap = this.getLengthMap();
    // map  this.props.entity.type -> map of field name to max length of fields

    const nameMaxLength: number = lengthMap.NAME;
    const descriptionMaxLength: number = lengthMap.DESCRIPTION;

    return (
      <DialogComponent
        title={titleMessage}
        mainButton={{
          label: "Save Item",
          isDisabled: this.props.submitting || this.props.invalid,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        closeFn={() => {
          this.props.reset();
          this.props.onClose();
        }}
      >
        <InputReduxFormField
          component={InputField}
          componentClass="input"
          name={EditSavedItemDialogImpl.FIELD_NAME}
          label="Name"
          placeholder={`Enter ${savedItem.typeDisplayName} name`}
          maxLength={nameMaxLength}
          validate={simpleFieldValidation}
        />

        <InputReduxFormField
          component={InputField}
          componentClass="textarea"
          name={EditSavedItemDialogImpl.FIELD_DESCRIPTION}
          label="Description"
          placeholder={`Enter ${savedItem.typeDisplayName} description`}
          maxLength={descriptionMaxLength}
        />

        {this.renderPrivacyCheckbox()}

        <ProjectTagInputReduxFormField
          name={EditSavedItemDialogImpl.FIELD_TAGS}
          label="Tags"
          projectId={savedItem.project.id}
        />

        {this.props.error && <Callout intent={CalloutIntent.Danger}>{this.props.error}</Callout>}
      </DialogComponent>
    );
  }
}

const EditSavedItemDialog = connect<
  IEditSavedItemDialogAppStateProps,
  IEditSavedItemDialogDispatchProps,
  IEditSavedItemDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: EditSavedItemDialogImpl.FORM_NAME,
  })(EditSavedItemDialogImpl) as any,
);

export { EditSavedItemDialog, EditSavedItemDialogImpl };
